
import api from "../services/axiosInstance";
import { backendPort } from '../components/Fungsiku';
import { useNotification } from './NotifPopup';
import { useEffect, useMemo, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import Register from './RegisterUser'; 
import ChangePassword from "./ChangePassword";
import EditProfilUser from "./EditProfilUser";
const ADMIN_PANEL_BOX_STYLE = {
  background: "linear-gradient(180deg, #f8fbff 0%, #f3f6fb 100%)",
  border: "1px solid #d8e0ee",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
};
const ADMIN_PANEL_BUTTON_STYLE = {
  background: "#0f2d5c",
  color: "#ffffff",
  border: "1px solid #0f2d5c",
  boxShadow: "0 4px 12px rgba(15, 45, 92, 0.28)",
};
const ADMIN_PANEL_BUTTON_CLASS = "multiBaris col-md-1 px-2 py-0.5 rounded text-xs font-medium";
const STATUS_USER_COLUMNS = [
  { key: "cabang", label: "CABANG", minWidth: 160 },
  { key: "nama_pegawai", label: "NAMA PEGAWAI", minWidth: 220 },
  { key: "username", label: "USERNAME", minWidth: 180 },
  { key: "user_level", label: "USER LEVEL", minWidth: 120 },
  { key: "status_akses", label: "STATUS AKSES", minWidth: 150 },
  { key: "status_aktivasi", label: "STATUS AKTIVASI", minWidth: 170 },
];

const escapeExcelHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

const sanitizeFilename = (value) => String(value || "laporan")
  .trim()
  .replace(/[\\/:*?"<>|]+/g, "_")
  .replace(/\s+/g, "_");

const downloadExcelRows = ({ headers = [], bodyRows = [], filename }) => {
  const headHtml = headers
    .map((header) => `<th style="background:#0f2d5c;color:#ffffff;font-weight:400;">${escapeExcelHtml(header)}</th>`)
    .join("");
  const bodyHtml = bodyRows
    .map((cells) => `<tr>${cells.map((cell) => `<td>${escapeExcelHtml(cell)}</td>`).join("")}</tr>`)
    .join("");
  const html = `\uFEFF<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${sanitizeFilename(filename)}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const formatStatusUserValue = (value, key) => {
  const text = String(value ?? "").trim().toUpperCase();
  if (!text) return "-Belum diketahui-";
  if (key === "status_aktivasi" && text === "NONAKTIF") return "NON AKTIF";
  return text;
};

const getStatusUserValue = (row, key) => {
  if (key === "status_akses" || key === "status_aktivasi") {
    return formatStatusUserValue(row?.[key], key);
  }
  return String(row?.[key] ?? "").trim() || "-";
};

const QR_IMAGE_WIDTH_PX = 800;
const QR_PREVIEW_SIZE_PX = 246;
const QR_PDF_SIZE_MM = 30;
const QR_EXCEL_IMAGE_SIZE_PX = 90;

const createQrDataUrl = (text) => QRCode.toDataURL(String(text || ""), {
  errorCorrectionLevel: "M",
  margin: 6,
  type: "image/png",
  width: QR_IMAGE_WIDTH_PX,
  color: {
    dark: "#000000",
    light: "#ffffff",
  },
});

export function AdminPage({authIdentity = {}, opsiCabang = []}){
  const [activePanel, setActivePanel] = useState(null);
  const [guardOpen, setGuardOpen] = useState(false);
  const [guardPassword, setGuardPassword] = useState("");
  const [guardError, setGuardError] = useState("");
  const [guardLoading, setGuardLoading] = useState(false);
  const [guardResolver, setGuardResolver] = useState(null);
  const [guardTitle, setGuardTitle] = useState("Konfirmasi Password");
  const kodeCabangUser = String(authIdentity?.kode_cabang || "").trim();
  const usernameInfo = String(authIdentity?.username || "").trim();
  const namaUser = String(authIdentity?.nama || "").trim();
  const userLevel = Number(authIdentity?.user_level ?? 0);
  const isLevel9 = Number(authIdentity?.user_level ?? 0) === 9;
  const isLevel8OrAbove = userLevel >= 8;
  const isPusatUser = kodeCabangUser === "C-1";
  const canAccessUserAdminMenus = isPusatUser && isLevel8OrAbove;
  const namaCabang = (
    isPusatUser
      ? "Pusat"
      : (
        (Array.isArray(opsiCabang) ? opsiCabang : []).find(
          (x) => String(x?.kode_cabang || "").trim() === kodeCabangUser
        )?.nama_cabang || kodeCabangUser || "-"
      )
  );

  const requestSubmitGuard = (title = "Konfirmasi Password") => (
    new Promise((resolve) => {
      setGuardTitle(title);
      setGuardPassword("");
      setGuardError("");
      setGuardResolver(() => resolve);
      setGuardOpen(true);
    })
  );

  const closeGuard = (passed) => {
    if (guardResolver) guardResolver(Boolean(passed));
    setGuardOpen(false);
    setGuardPassword("");
    setGuardError("");
    setGuardLoading(false);
    setGuardResolver(null);
  };

  const confirmGuard = async () => {
    try {
      if (!guardPassword.trim()) {
        setGuardError("Password wajib diisi");
        return;
      }
      setGuardLoading(true);
      setGuardError("");
      await api.post(`${backendPort}/api/auth/verify-password`, { password: guardPassword });
      closeGuard(true);
    } catch (err) {
      setGuardLoading(false);
      setGuardError(err?.response?.data?.message || "Password tidak valid");
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const navState = window.__admin_page_nav && typeof window.__admin_page_nav === "object"
      ? window.__admin_page_nav
      : null;
    if (!navState?.panel) return;
    setActivePanel(String(navState.panel || "").trim() || null);
    window.__admin_page_nav = null;
  }, []);

  //PopupHandler
    return(
        <>
        {/* Header */}
        <div className="col-md-12 d-flex justify-content-between">
          <div className="col-md-10">
            <h1 className="text-2xl font-semibold">ADMIN PAGE</h1>
            <p className="text-gray-500 text-sm">
              Welcome {namaUser}.
            </p>
          </div>
        </div>

        {/* Bottom Section */}
        <section className="grid grid-cols-1">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg p-4 border">
            <h3 className="text-2xl font-medium">🔢 SETTING</h3>
            <p className="text-2s font-medium mb-4 text-red-800">{`Unit: ${namaCabang}`}</p>
            <div className="space-y-3 text-sm">
              <div className="flex items-center"><span className="mr-3">Users Setting</span><div className="flex-grow h-px bg-gray-300"></div></div>     
            {canAccessUserAdminMenus && <div><RegisterUserDialog activePanel={activePanel} setActivePanel={setActivePanel} requestSubmitGuard={requestSubmitGuard} /></div>}
            {canAccessUserAdminMenus && <div><EditProfilUserDialog activePanel={activePanel} setActivePanel={setActivePanel} requestSubmitGuard={requestSubmitGuard} /></div>}
            <div><UbahPassword activePanel={activePanel} setActivePanel={setActivePanel} usernameInfo={usernameInfo} requestSubmitGuard={requestSubmitGuard} /></div>  
            {canAccessUserAdminMenus && <div><ResetPassword activePanel={activePanel} setActivePanel={setActivePanel} requestSubmitGuard={requestSubmitGuard} /></div>}
            {canAccessUserAdminMenus && <div><LockUnlockUser activePanel={activePanel} setActivePanel={setActivePanel} requestSubmitGuard={requestSubmitGuard} /></div>}
            {isLevel8OrAbove && <div><GenerateQrCodeUser activePanel={activePanel} setActivePanel={setActivePanel} opsiCabang={opsiCabang} /></div>}
            {isLevel8OrAbove && <div><GenerateQrCodeMt activePanel={activePanel} setActivePanel={setActivePanel} opsiCabang={opsiCabang} /></div>}
            {/**absensiadmindialog sekarang ada di dashboard*/}
            {false && <div><AbsensiAdminDialog activePanel={activePanel} setActivePanel={setActivePanel} requestSubmitGuard={requestSubmitGuard} /></div>}            
            {isLevel8OrAbove && <div className="flex items-center"><span className="mr-3">Tasks</span><div className="flex-grow h-px bg-gray-300"></div></div>}
            {/**TOMBOL GENERATE SALDO */}
            {isLevel9 && <div><Generate activePanel={activePanel} setActivePanel={setActivePanel} /></div>}
            {isLevel9 && <div><AdjustTanggal activePanel={activePanel} setActivePanel={setActivePanel} /></div>}
            {isLevel8OrAbove && <div><CetakAuditTrail activePanel={activePanel} setActivePanel={setActivePanel} /></div>}
      
            </div>
          </div>
          
        </section>
      {guardOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white p-4 rounded w-96">
            <h5>{guardTitle}</h5>
            <p className="mb-2">Masukkan password Anda untuk melanjutkan submit.</p>
            <input
              type="password"
              className="form-control form-control-sm"
              name="admin_guard_password"
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              value={guardPassword}
              onChange={(e) => setGuardPassword(e.target.value)}
              placeholder="Password..."
              autoFocus
            />
            {guardError ? <div className="text-danger small mt-2">{guardError}</div> : null}
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-secondary btn-sm" onClick={() => closeGuard(false)} disabled={guardLoading}>Batal</button>
              <button className="btn btn-primary btn-sm" onClick={confirmGuard} disabled={guardLoading}>
                {guardLoading ? "Memverifikasi..." : "Lanjutkan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
    )
}

function RegisterUserDialog({ activePanel, setActivePanel, requestSubmitGuard }){
  const panelKey = "register_user";
  const open = activePanel === panelKey;
  const info="Pendaftaran User Baru oleh Admin."; 
  const label="REGISTER USER BARU";

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
      <div 
        onClick = {()=>setActivePanel(open ? null : panelKey)}
        style = {{ ...ADMIN_PANEL_BUTTON_STYLE, cursor : 'pointer'}}
        className={ADMIN_PANEL_BUTTON_CLASS}
      >{label}</div>
      <div className="col-md-10 text-gray-600">{info}</div>      
      </div>
      {open && <div className="row mt-2">
        <Register requestSubmitGuard={requestSubmitGuard} />
      </div>
      }
    </div>
    )
  
  }

function EditProfilUserDialog({ activePanel, setActivePanel, requestSubmitGuard }){
  const panelKey = "edit_profil_user";
  const open = activePanel === panelKey;
  const info="Edit profil user oleh admin.";
  const label="EDIT PROFIL USER";

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
      <div
        onClick = {()=>setActivePanel(open ? null : panelKey)}
        style = {{ ...ADMIN_PANEL_BUTTON_STYLE, cursor : 'pointer'}}
        className={ADMIN_PANEL_BUTTON_CLASS}
      >{label}</div>
      <div className="col-md-10 text-gray-600">{info}</div>
      </div>
      {open && <div className="row mt-2">
        <EditProfilUser requestSubmitGuard={requestSubmitGuard} />
      </div>
      }
    </div>
    )
}

function UbahPassword({ activePanel, setActivePanel, usernameInfo, requestSubmitGuard }){
  const panelKey = "ubah_password";
  const open = activePanel === panelKey;
  const info="Ganti Password User"; 
  const label="GANTI PASSWORD";

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
      <div 
        onClick = {()=>setActivePanel(open ? null : panelKey)}
        style = {{ ...ADMIN_PANEL_BUTTON_STYLE, cursor : 'pointer'}}
        className={ADMIN_PANEL_BUTTON_CLASS}
      >{label}</div>
      <div className="col-md-10 text-gray-600">{info}</div>      
      </div>
      {open && <div className="row mt-2">
        <ChangePassword usernameInfo={usernameInfo} requestSubmitGuard={requestSubmitGuard} />
      </div>
      }
    </div>
    )
  
  }

function ResetPassword({ activePanel, setActivePanel, requestSubmitGuard }){
  const panelKey = "reset_password";
  const open = activePanel === panelKey;
  const [targetname, setTargetName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const info="Reset Password oleh admin."; 
  const label="RESET PASSWORD";

  async function handleReset(targetname, newPassword){
    const cleanTarget = targetname.trim();
    const cleanPassword = newPassword.trim();
    const passed = await requestSubmitGuard("Guard Submit: Reset Password");
    if (!passed) return;
    if (!window.confirm(`Reset password user ${cleanTarget}?`)) return;

    try {
      const res = await api.put(`${backendPort}/api/auth/reset-password/${cleanTarget}`, { newPassword: cleanPassword });
      alert(res.data.message);
      setTargetName("");
      setNewPassword("");
    } catch (err) {
      alert(err.response?.data?.message || "Gagal reset password");
    }
  };
   
  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
      <div 
        onClick = {()=>setActivePanel(open ? null : panelKey)}
        style = {{ ...ADMIN_PANEL_BUTTON_STYLE, cursor : 'pointer'}}
        className={ADMIN_PANEL_BUTTON_CLASS}
      >{label}</div>
      <div className="col-md-10 text-gray-600">{info}</div>      
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-2">
              <label className="form-label mb-1">Username</label>
              <input
                className="form-control form-control-sm"
                name="reset_password_target_username"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                placeholder="username ..."
                value={targetname}
                onChange={e => {setTargetName(e.target.value);}}
              />
            </div>

            <div className="col-2">
              <label className="form-label mb-1">Password Baru</label>
              <div className="input-group input-group-sm">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control"
                  name="reset_password_new_password"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  placeholder="Password baru ..."
                  value={newPassword}
                  onChange={e => {setNewPassword(e.target.value);}}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? "Sembunyikan password baru" : "Tampilkan password baru"}
                >
                  <i className={`bi ${showNewPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            <div className="col"></div>

            <div className="col-2">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={() => {
                  if (targetname.trim() && newPassword.trim()) {
                    handleReset(targetname, newPassword);
                  } else {
                    alert("Username dan password baru wajib diisi");
                  }
                }}
              >
                Submit
              </button>
            </div>
          </div>

          <div className="mt-2 small text-muted">
            <div>Aturan password:</div>
            <div>1. Password baru minimal 6 karakter.</div>
            <div>2. Username target wajib diisi dan harus terdaftar.</div>
          </div>
        </div>
      </div>
      }
    </div>
    )
}  

function LockUnlockUser({ activePanel, setActivePanel, requestSubmitGuard }){
  const panelKey = "lock_unlock_user";
  const open = activePanel === panelKey;
  const [opsiUser, setOpsiUser] = useState([]);
  const [username, setUsername] = useState("");
  const [akses, setAkses] = useState("");
  const [statusUser, setStatusUser] = useState("");
  const [resetDevice, setResetDevice] = useState("TANPA RESET");
  const [loading, setLoading] = useState(false);
  const [loadingOpsi, setLoadingOpsi] = useState(false);
  const [loadingDataUser, setLoadingDataUser] = useState(false);
  const [loadingStatusList, setLoadingStatusList] = useState(false);
  const [showStatusListModal, setShowStatusListModal] = useState(false);
  const [statusListRows, setStatusListRows] = useState([]);
  const [statusListIncludeText, setStatusListIncludeText] = useState("");
  const [statusListExcludeText, setStatusListExcludeText] = useState("");
  const [statusListPageSize, setStatusListPageSize] = useState("25");
  const [statusListPage, setStatusListPage] = useState(1);
  const [statusListSortBy, setStatusListSortBy] = useState("cabang");
  const [statusListSortDir, setStatusListSortDir] = useState("asc");
  const dataUserRequestRef = useRef(0);
  const { showNotif } = useNotification();
  const info="Aktifasi dan perubahan status akses.";
  const label="PERUBAHAN STATUS USER";

  const filteredStatusListRows = useMemo(() => {
    const include = String(statusListIncludeText || "").trim().toLowerCase();
    const exclude = String(statusListExcludeText || "").trim().toLowerCase();
    const filtered = statusListRows.filter((item) => {
      const text = STATUS_USER_COLUMNS
        .map((column) => getStatusUserValue(item, column.key))
        .join(" ")
        .toLowerCase();
      if (include && !text.includes(include)) return false;
      if (exclude && text.includes(exclude)) return false;
      return true;
    });
    return [...filtered].sort((left, right) => {
      const leftValue = getStatusUserValue(left, statusListSortBy);
      const rightValue = getStatusUserValue(right, statusListSortBy);
      const compared = String(leftValue).localeCompare(String(rightValue), "id", {
        numeric: true,
        sensitivity: "base",
      });
      return statusListSortDir === "asc" ? compared : -compared;
    });
  }, [statusListExcludeText, statusListIncludeText, statusListRows, statusListSortBy, statusListSortDir]);

  const statusListEffectivePageSize = statusListPageSize === "all"
    ? Math.max(filteredStatusListRows.length, 1)
    : Math.max(Number(statusListPageSize || 25), 1);
  const statusListTotalPages = Math.max(Math.ceil(filteredStatusListRows.length / statusListEffectivePageSize), 1);
  const statusListSafePage = Math.min(statusListPage, statusListTotalPages);
  const statusListStartIndex = (statusListSafePage - 1) * statusListEffectivePageSize;
  const statusListPagedRows = filteredStatusListRows.slice(
    statusListStartIndex,
    statusListStartIndex + statusListEffectivePageSize
  );

  useEffect(() => {
    setStatusListPage(1);
  }, [statusListExcludeText, statusListIncludeText, statusListPageSize, statusListRows]);

  const resetStatusForm = () => {
    setAkses("");
    setStatusUser("");
    setResetDevice("TANPA RESET");
  };

  async function loadOpsiUser() {
    try {
      setLoadingOpsi(true);
      const { data } = await api.get(`${backendPort}/api/auth/load_opsi_nama_pegawai`);
      const list = Array.isArray(data?.data) ? data.data : [];
      setOpsiUser(list.map((x) => String(x?.username || "").trim()).filter(Boolean));
    } catch (err) {
      console.error(err);
      showNotif("error", "Gagal memuat daftar user");
    } finally {
      setLoadingOpsi(false);
    }
  }

  async function loadDataUser(targetUsername) {
    const cleanUser = String(targetUsername || "").trim();
    const requestId = dataUserRequestRef.current + 1;
    dataUserRequestRef.current = requestId;
    if (!cleanUser) {
      resetStatusForm();
      setLoadingDataUser(false);
      return;
    }
    try {
      setLoadingDataUser(true);
      const { data } = await api.get(`${backendPort}/api/auth/load_data_user/${encodeURIComponent(cleanUser)}`);
      const row = data?.data || {};
      const nextAkses = String(row?.akses ?? "").trim().toUpperCase();
      const nextStatus = String(row?.status ?? "").trim().toUpperCase();
      if (dataUserRequestRef.current !== requestId) return;
      setAkses(["LOCK", "UNLOCK"].includes(nextAkses) ? nextAkses : "");
      setStatusUser(["AKTIF", "NONAKTIF"].includes(nextStatus) ? nextStatus : "");
      setResetDevice("TANPA RESET");
    } catch (err) {
      console.error(err);
      if (dataUserRequestRef.current !== requestId) return;
      resetStatusForm();
      showNotif("error", err?.response?.data?.message || "Gagal memuat data user");
    } finally {
      if (dataUserRequestRef.current === requestId) setLoadingDataUser(false);
    }
  }

  async function handleSubmit() {
    const cleanUser = String(username || "").trim();
    if (!cleanUser || !akses || !statusUser || !resetDevice) {
      showNotif("error", "Data belum lengkap");
      return;
    }
    try {
      const passed = await requestSubmitGuard("Guard Submit: Perubahan Status User");
      if (!passed) return;
      setLoading(true);
      const res = await api.put(`${backendPort}/api/auth/update_akses_user/${encodeURIComponent(cleanUser)}`, {
        akses,
        status: statusUser,
        reset_device: resetDevice,
      });
      showNotif("success", res.data?.message || "Perubahan status user berhasil");
      setUsername("");
      resetStatusForm();
    } catch (err) {
      console.error(err);
      showNotif("error", err?.response?.data?.message || "Perubahan status user gagal");
    } finally {
      setLoading(false);
    }
  }

  async function loadStatusUserList() {
    try {
      setLoadingStatusList(true);
      const { data } = await api.get(`${backendPort}/api/auth/daftar_status_user`, { timeout: 120000 });
      setStatusListRows(Array.isArray(data?.rows) ? data.rows : []);
      setShowStatusListModal(true);
    } catch (err) {
      console.error(err);
      showNotif("error", err?.response?.data?.message || "Gagal memuat daftar status user");
    } finally {
      setLoadingStatusList(false);
    }
  }

  const handleStatusListSort = (columnKey) => {
    if (statusListSortBy === columnKey) {
      setStatusListSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setStatusListSortBy(columnKey);
    setStatusListSortDir("asc");
  };

  const downloadStatusUserRows = (exportRows, suffix) => {
    downloadExcelRows({
      headers: ["NO", ...STATUS_USER_COLUMNS.map((column) => column.label)],
      bodyRows: (Array.isArray(exportRows) ? exportRows : []).map((item, index) => [
        index + 1,
        ...STATUS_USER_COLUMNS.map((column) => getStatusUserValue(item, column.key)),
      ]),
      filename: `DAFTAR_STATUS_USER_${suffix}`,
    });
  };

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
        <div
          onClick={() => {
            const willOpen = !open;
            setActivePanel(willOpen ? panelKey : null);
            if (willOpen) loadOpsiUser();
          }}
          style={{ ...ADMIN_PANEL_BUTTON_STYLE, cursor: 'pointer' }}
          className={ADMIN_PANEL_BUTTON_CLASS}
        >{label}</div>
        <div className="col-md-10 text-gray-600">{info}</div>
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-3">
              <label className="form-label mb-1">Username</label>
              <select
                className="form-select form-select-sm"
                name="lock_unlock_target_username"
                autoComplete="off"
                value={username}
                onChange={(e) => {
                  const nextUsername = e.target.value;
                  setUsername(nextUsername);
                  loadDataUser(nextUsername);
                }}
                disabled={loading || loadingOpsi || loadingDataUser}
              >
                <option value="">-Pilih Username-</option>
                {opsiUser.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Akses</label>
              <select
                className="form-select form-select-sm"
                value={akses}
                onChange={(e) => setAkses(e.target.value)}
                disabled={!username || loading || loadingDataUser}
              >
                <option value="">-Belum diketahui-</option>
                <option value="UNLOCK">UNLOCK</option>
                <option value="LOCK">LOCK</option>
              </select>
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Aktifasi</label>
              <select
                className="form-select form-select-sm"
                value={statusUser}
                onChange={(e) => setStatusUser(e.target.value)}
                disabled={!username || loading || loadingDataUser}
              >
                <option value="">-Belum diketahui-</option>
                <option value="AKTIF">AKTIF</option>
                <option value="NONAKTIF">NON AKTIF</option>
              </select>
            </div>
            <div className="col"></div>
            <div className="col-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm w-100"
                disabled={loadingStatusList}
                onClick={loadStatusUserList}
              >
                {loadingStatusList ? "Memuat..." : "Daftar Status User"}
              </button>
            </div>
            <div className="col-2">
              <button
                type="button"
                className="btn btn-primary btn-sm w-100"
                disabled={loading || loadingOpsi || loadingDataUser}
                onClick={handleSubmit}
              >
                {loading ? "Memproses..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>}
      {showStatusListModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ zIndex: 1300 }}>
            <div
              className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
              style={{ width: "min(1400px, 98vw)", maxWidth: "98vw" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title">DAFTAR STATUS USER</h5>
                    <div className="text-muted small">Sumber data: sp_daftar_status_user</div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setShowStatusListModal(false)} />
                </div>
                <div className="modal-body p-3">
                  <section className="bg-white rounded-lg p-3 border">
                    <div className="d-flex justify-content-end gap-2 mb-3 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => downloadStatusUserRows(filteredStatusListRows, "preview")}
                      >
                        Download Preview
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => downloadStatusUserRows(statusListRows, "all")}
                      >
                        Download All
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowStatusListModal(false)}
                      >
                        Close
                      </button>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-12 col-md-3">
                        <label className="form-label mb-1">Filter Mengandung Teks</label>
                        <input
                          className="form-control form-control-sm"
                          value={statusListIncludeText}
                          onChange={(event) => setStatusListIncludeText(event.target.value)}
                          placeholder="baris mengandung teks.."
                        />
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="form-label mb-1">Filter Negasi Teks</label>
                        <input
                          className="form-control form-control-sm"
                          value={statusListExcludeText}
                          onChange={(event) => setStatusListExcludeText(event.target.value)}
                          placeholder="baris tidak mengandung teks.."
                        />
                      </div>
                      <div className="col-12 col-md-3" />
                      <div className="col-12 col-md-3">
                        <label className="form-label mb-1">
                          {`Ditampilkan ${statusListPagedRows.length} | Total: ${filteredStatusListRows.length}`}
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={statusListPageSize}
                          onChange={(event) => setStatusListPageSize(event.target.value)}
                        >
                          {[10, 25, 50, 100].map((size) => (
                            <option key={size} value={String(size)}>{size}</option>
                          ))}
                          <option value="all">Semua</option>
                        </select>
                      </div>
                    </div>

                    <div
                      className="border rounded report-scroll-hover-x"
                      style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}
                    >
                      <table className="table table-sm mb-0 report34-table">
                        <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                          <tr>
                            <th
                              style={{
                                minWidth: 50,
                                background: "#0f2d5c",
                                color: "#ffffff",
                                fontWeight: 400,
                              }}
                            >
                              NO
                            </th>
                            {STATUS_USER_COLUMNS.map((column) => (
                              <th
                                key={column.key}
                                style={{
                                  minWidth: column.minWidth,
                                  background: "#0f2d5c",
                                  color: "#ffffff",
                                  fontWeight: 400,
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 text-decoration-none"
                                  style={{
                                    color: "#ffffff",
                                    fontWeight: 400,
                                    width: "100%",
                                    textAlign: "left",
                                  }}
                                  onClick={() => handleStatusListSort(column.key)}
                                >
                                  {`${column.label}${statusListSortBy === column.key ? (statusListSortDir === "asc" ? " ▲" : " ▼") : ""}`}
                                </button>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {statusListPagedRows.length === 0 && (
                            <tr>
                              <td colSpan={STATUS_USER_COLUMNS.length + 1} className="text-center text-muted py-3">
                                Tidak ada data.
                              </td>
                            </tr>
                          )}
                          {statusListPagedRows.map((item, index) => (
                            <tr key={`status-user-${item.username || statusListStartIndex + index}`}>
                              <td style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}>
                                {statusListStartIndex + index + 1}
                              </td>
                              {STATUS_USER_COLUMNS.map((column) => (
                                <td
                                  key={`${item.username || statusListStartIndex + index}-${column.key}`}
                                  style={{
                                    paddingTop: "0.25rem",
                                    paddingBottom: "0.25rem",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getStatusUserValue(item, column.key)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center gap-2 mt-3">
                      <div className="small text-muted">{`Halaman ${statusListSafePage} dari ${statusListTotalPages}`}</div>
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={statusListSafePage <= 1}
                          onClick={() => setStatusListPage((current) => Math.max(current - 1, 1))}
                        >
                          Sebelumnya
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={statusListSafePage >= statusListTotalPages}
                          onClick={() => setStatusListPage((current) => Math.min(current + 1, statusListTotalPages))}
                        >
                          Berikutnya
                        </button>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1290 }} />
        </>
      )}
    </div>
  );
}

function GenerateQrCodeUser({ activePanel, setActivePanel, opsiCabang = [] }) {
  const panelKey = "generate_qr_code_user";
  const open = activePanel === panelKey;
  const [opsiUser, setOpsiUser] = useState([]);
  const [activeUserRows, setActiveUserRows] = useState([]);
  const [username, setUsername] = useState("");
  const [userDetail, setUserDetail] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [loadingOpsi, setLoadingOpsi] = useState(false);
  const [loadingDataUser, setLoadingDataUser] = useState(false);
  const [loadingMassal, setLoadingMassal] = useState(false);
  const dataUserRequestRef = useRef(0);
  const { showNotif } = useNotification();
  const info = "Generate QR Code berdasarkan username aktif.";
  const label = "GENERATE QR USERS";

  const cabangByKode = useMemo(() => {
    const map = new Map();
    (Array.isArray(opsiCabang) ? opsiCabang : []).forEach((item) => {
      const kode = String(item?.kode_cabang || "").trim();
      const nama = String(item?.nama_cabang || "").trim();
      if (kode) map.set(kode, nama || kode);
    });
    return map;
  }, [opsiCabang]);

  const displayDetail = useMemo(() => {
    const kodeCabang = String(userDetail?.kode_cabang || "").trim();
    return {
      username: String(username || "").trim(),
      namaPegawai: String(userDetail?.nama || userDetail?.nama_pegawai || "").trim(),
      cabang: cabangByKode.get(kodeCabang) || String(userDetail?.cabang || userDetail?.nama_cabang || kodeCabang || "").trim(),
      posisi: String(userDetail?.jabatan || userDetail?.posisi || "").trim(),
      userLevel: String(userDetail?.user_level ?? "").trim(),
    };
  }, [cabangByKode, userDetail, username]);
  const resetQrForm = () => {
    setUserDetail(null);
    setQrDataUrl("");
  };

  async function loadActiveUsers() {
    try {
      setLoadingOpsi(true);
      const { data } = await api.get(`${backendPort}/api/auth/daftar_status_user`, { timeout: 120000 });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const activeRowsByUsername = new Map();
      rows
        .filter((row) => String(row?.status_aktivasi || "").trim().toUpperCase() === "AKTIF")
        .forEach((row) => {
          const cleanUsername = String(row?.username || "").trim();
          if (cleanUsername && !activeRowsByUsername.has(cleanUsername)) {
            activeRowsByUsername.set(cleanUsername, { ...row, username: cleanUsername });
          }
        });
      const activeRows = [...activeRowsByUsername.values()]
        .sort((left, right) => String(left.username).localeCompare(String(right.username), "id", { sensitivity: "base" }));
      setActiveUserRows(activeRows);
      setOpsiUser(activeRows.map((row) => row.username));
    } catch (err) {
      console.error(err);
      showNotif("error", err?.response?.data?.message || "Gagal memuat daftar user aktif");
    } finally {
      setLoadingOpsi(false);
    }
  }

  async function loadDataUser(targetUsername) {
    const cleanUser = String(targetUsername || "").trim();
    const requestId = dataUserRequestRef.current + 1;
    dataUserRequestRef.current = requestId;
    resetQrForm();
    if (!cleanUser) {
      setLoadingDataUser(false);
      return;
    }
    try {
      setLoadingDataUser(true);
      const { data } = await api.get(`${backendPort}/api/auth/load_data_user/${encodeURIComponent(cleanUser)}`);
      if (dataUserRequestRef.current !== requestId) return;
      setUserDetail(data?.data || null);
    } catch (err) {
      console.error(err);
      if (dataUserRequestRef.current !== requestId) return;
      showNotif("error", err?.response?.data?.message || "Gagal memuat data user");
    } finally {
      if (dataUserRequestRef.current === requestId) setLoadingDataUser(false);
    }
  }

  const handleGenerate = async () => {
    const cleanUser = String(username || "").trim();
    if (!cleanUser) {
      showNotif("error", "Username wajib dipilih");
      return;
    }
    if (!userDetail) {
      showNotif("error", "Data user belum valid");
      return;
    }
    try {
      setQrDataUrl(await createQrDataUrl(cleanUser));
    } catch (err) {
      console.error(err);
      showNotif("error", err?.message || "Gagal generate QR Code");
    }
  };

  const handleDownloadPdf = () => {
    if (!qrDataUrl) {
      showNotif("error", "Generate QR Code terlebih dahulu");
      return;
    }
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const qrSize = QR_PDF_SIZE_MM;
    const qrX = (pageWidth - qrSize) / 2;
    let y = 22;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("QR CODE USER", pageWidth / 2, y, { align: "center" });
    y += 10;
    doc.addImage(qrDataUrl, "PNG", qrX, y, qrSize, qrSize);
    y += qrSize + 10;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    [
      ["Username", displayDetail.username],
      ["Nama Pegawai", displayDetail.namaPegawai],
      ["Cabang", displayDetail.cabang],
      ["Posisi", displayDetail.posisi],
      ["User Level", displayDetail.userLevel],
    ].forEach(([labelText, value]) => {
      doc.text(`${labelText}: ${String(value || "-")}`, 24, y);
      y += 7;
    });
    doc.save(`QR_CODE_${sanitizeFilename(displayDetail.username)}.pdf`);
  };

  const handleDownloadMassalExcel = async () => {
    if (!activeUserRows.length) {
      showNotif("error", "Daftar user aktif belum tersedia");
      return;
    }

    try {
      setLoadingMassal(true);
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "simPua";
      workbook.created = new Date();
      const worksheet = workbook.addWorksheet("QR Code Pegawai");

      worksheet.columns = [
        { header: "No", key: "no", width: 8 },
        { header: "Username", key: "username", width: 24 },
        { header: "Nama Pegawai", key: "nama_pegawai", width: 32 },
        { header: "Cabang", key: "cabang", width: 28 },
        { header: "User Level", key: "user_level", width: 14 },
        { header: "QR Code", key: "qr_code", width: 18 },
      ];
      worksheet.views = [{ state: "frozen", ySplit: 1 }];
      worksheet.getRow(1).height = 24;
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F2D5C" } };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      for (const [index, userRow] of activeUserRows.entries()) {
        const cleanUsername = String(userRow?.username || "").trim();
        const excelRow = worksheet.addRow({
          no: index + 1,
          username: cleanUsername,
          nama_pegawai: String(userRow?.nama_pegawai || "").trim(),
          cabang: String(userRow?.cabang || "").trim(),
          user_level: userRow?.user_level ?? "",
          qr_code: "",
        });
        excelRow.height = 84;
        excelRow.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: cell.col === 6 ? "center" : "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        const qrDataUrlForUser = await createQrDataUrl(cleanUsername);
        const imageId = workbook.addImage({ base64: qrDataUrlForUser, extension: "png" });
        worksheet.addImage(imageId, {
          tl: { col: 5.18, row: excelRow.number - 0.92 },
          ext: { width: QR_EXCEL_IMAGE_SIZE_PX, height: QR_EXCEL_IMAGE_SIZE_PX },
          editAs: "oneCell",
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `QR_CODE_SEMUA_PEGAWAI_${Date.now()}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      showNotif("success", `Excel QR Code ${activeUserRows.length} pegawai berhasil dibuat`);
    } catch (err) {
      console.error(err);
      showNotif("error", err?.message || "Gagal membuat Excel QR Code massal");
    } finally {
      setLoadingMassal(false);
    }
  };

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
        <div
          onClick={() => {
            const willOpen = !open;
            setActivePanel(willOpen ? panelKey : null);
            if (willOpen) loadActiveUsers();
          }}
          style={{ ...ADMIN_PANEL_BUTTON_STYLE, cursor: "pointer" }}
          className={ADMIN_PANEL_BUTTON_CLASS}
        >{label}</div>
        <div className="col-md-10 text-gray-600">{info}</div>
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Username Aktif</label>
              <select
                className="form-select form-select-sm"
                value={username}
                disabled={loadingOpsi || loadingDataUser}
                onChange={(event) => {
                  const nextUsername = event.target.value;
                  setUsername(nextUsername);
                  loadDataUser(nextUsername);
                }}
              >
                <option value="">-Pilih Username-</option>
                {opsiUser.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label mb-1">Nama Pegawai</label>
              <input className="form-control form-control-sm" value={displayDetail.namaPegawai || ""} readOnly placeholder="-Belum diketahui-" />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label mb-1">Cabang</label>
              <input className="form-control form-control-sm" value={displayDetail.cabang || ""} readOnly placeholder="-Belum diketahui-" />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label mb-1">Posisi</label>
              <input className="form-control form-control-sm" value={displayDetail.posisi || ""} readOnly placeholder="-Belum diketahui-" />
            </div>
            <div className="col-12 col-md-1">
              <label className="form-label mb-1">User Level</label>
              <input className="form-control form-control-sm" value={displayDetail.userLevel || ""} readOnly placeholder="-" />
            </div>
            <div className="col-12 col-md-2">
              <button
                type="button"
                className="btn btn-primary btn-sm w-100"
                disabled={loadingOpsi || loadingDataUser || loadingMassal || !username || !userDetail}
                onClick={handleGenerate}
              >
                {loadingDataUser ? "Memuat..." : "Generate"}
              </button>
            </div>
          </div>
          <div className="row g-2 align-items-center mt-2">
            <div className="col-12 col-md-3">
              <button
                type="button"
                className="btn btn-success btn-sm w-100"
                disabled={loadingOpsi || loadingMassal || !activeUserRows.length}
                onClick={handleDownloadMassalExcel}
              >
                {loadingMassal ? "Membuat Excel..." : "Download Excel Semua Pegawai"}
              </button>
            </div>
            <div className="col-12 col-md-9 small text-muted">
              {loadingOpsi ? "Memuat daftar pegawai aktif..." : `${activeUserRows.length} pegawai aktif siap dibuat QR Code.`}
            </div>
          </div>

          {qrDataUrl && (
            <div className="mt-3 d-flex flex-column align-items-start">
              <div className="border rounded bg-white p-3 text-center" style={{ width: QR_PREVIEW_SIZE_PX + 32 }}>
                <div className="small text-muted mb-2">Preview QR Code</div>
                <img
                  src={qrDataUrl}
                  alt={`QR Code ${displayDetail.username}`}
                  style={{
                    width: QR_PREVIEW_SIZE_PX,
                    height: QR_PREVIEW_SIZE_PX,
                    imageRendering: "pixelated",
                  }}
                />
              </div>
              <button type="button" className="btn btn-success btn-sm mt-2" onClick={handleDownloadPdf}>
                Download PDF
              </button>
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}

function GenerateQrCodeMt({ activePanel, setActivePanel, opsiCabang = [] }) {
  const panelKey = "generate_qr_code_mt";
  const open = activePanel === panelKey;
  const [mode, setMode] = useState("semua");
  const [mtRows, setMtRows] = useState([]);
  const [kodeCabang, setKodeCabang] = useState("");
  const [idMt, setIdMt] = useState("");
  const [loadingMt, setLoadingMt] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const { showNotif } = useNotification();
  const info = "Generate QR Code berdasarkan nopol MT.";
  const label = "GENERATE QR MT";

  const cabangByKode = useMemo(() => {
    const map = new Map();
    (Array.isArray(opsiCabang) ? opsiCabang : []).forEach((item) => {
      const kode = String(item?.kode_cabang || "").trim();
      const nama = String(item?.nama_cabang || "").trim();
      if (kode) map.set(kode, nama || kode);
    });
    return map;
  }, [opsiCabang]);

  const normalizedMtRows = useMemo(() => (
    (Array.isArray(mtRows) ? mtRows : [])
      .map((row) => {
        const kode = String(row?.kode_cabang_aktif || row?.kode_cabang || "").trim();
        return {
          id_mt: String(row?.id_mt || "").trim(),
          nopol: String(row?.nopol || "").trim(),
          kode_cabang: kode,
          cabang: cabangByKode.get(kode) || String(row?.nama_cabang || row?.cabang || kode || "").trim(),
          kapasitas: String(row?.kapasitas ?? "").trim(),
          kategori: String(row?.kategori || "").trim(),
          status: String(row?.status || "").trim(),
        };
      })
      .filter((row) => row.id_mt && row.nopol && row.status.toLowerCase() !== "hapus")
      .sort((left, right) => left.nopol.localeCompare(right.nopol, "id", { sensitivity: "base" }))
  ), [cabangByKode, mtRows]);

  const cabangOptions = useMemo(() => {
    const rows = normalizedMtRows
      .map((row) => ({ kode_cabang: row.kode_cabang, nama_cabang: row.cabang || row.kode_cabang }))
      .filter((row) => row.kode_cabang);
    const unique = new Map();
    rows.forEach((row) => {
      if (!unique.has(row.kode_cabang)) unique.set(row.kode_cabang, row);
    });
    return [...unique.values()].sort((left, right) => String(left.nama_cabang).localeCompare(String(right.nama_cabang), "id", { sensitivity: "base" }));
  }, [normalizedMtRows]);

  const selectedMt = useMemo(
    () => normalizedMtRows.find((row) => row.id_mt === idMt) || null,
    [idMt, normalizedMtRows]
  );

  const targetRows = useMemo(() => {
    if (mode === "cabang") {
      return normalizedMtRows.filter((row) => row.kode_cabang === kodeCabang);
    }
    if (mode === "satuan") {
      return selectedMt ? [selectedMt] : [];
    }
    return normalizedMtRows;
  }, [kodeCabang, mode, normalizedMtRows, selectedMt]);

  async function loadMtRows() {
    try {
      setLoadingMt(true);
      const { data } = await api.get(`${backendPort}/api/main/mt_profil`, { timeout: 120000 });
      setMtRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setMtRows([]);
      showNotif("error", err?.response?.data?.message || "Gagal memuat daftar MT");
    } finally {
      setLoadingMt(false);
    }
  }

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setKodeCabang("");
    setIdMt("");
  };

  const addMtPdfText = (doc, rows, startY) => {
    let y = startY;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    rows.forEach(([labelText, value]) => {
      doc.text(`${labelText}: ${String(value || "-")}`, 24, y);
      y += 7;
    });
  };

  const handleDownloadSatuanPdf = async () => {
    if (!selectedMt) {
      showNotif("error", "Pilih nopol MT terlebih dahulu");
      return;
    }
    try {
      setLoadingExport(true);
      const qrDataUrlMt = await createQrDataUrl(selectedMt.nopol);
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const qrSize = QR_PDF_SIZE_MM;
      const qrX = (pageWidth - qrSize) / 2;
      let y = 22;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("QR CODE MT", pageWidth / 2, y, { align: "center" });
      y += 10;
      doc.addImage(qrDataUrlMt, "PNG", qrX, y, qrSize, qrSize);
      y += qrSize + 10;
      addMtPdfText(doc, [
        ["Nopol", selectedMt.nopol],
        ["Kapasitas", selectedMt.kapasitas],
        ["Kategori", selectedMt.kategori],
      ], y);
      doc.save(`QR_CODE_MT_${sanitizeFilename(selectedMt.nopol)}.pdf`);
    } catch (err) {
      console.error(err);
      showNotif("error", err?.message || "Gagal membuat PDF QR Code MT");
    } finally {
      setLoadingExport(false);
    }
  };

  const handleDownloadMassalExcel = async () => {
    if (mode === "cabang" && !kodeCabang) {
      showNotif("error", "Pilih cabang terlebih dahulu");
      return;
    }
    if (!targetRows.length) {
      showNotif("error", "Data MT tidak tersedia");
      return;
    }

    try {
      setLoadingExport(true);
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "simPua";
      workbook.created = new Date();
      const worksheet = workbook.addWorksheet("QR Code MT");

      worksheet.columns = [
        { header: "No", key: "no", width: 8 },
        { header: "Nopol", key: "nopol", width: 20 },
        { header: "Cabang", key: "cabang", width: 28 },
        { header: "Kapasitas", key: "kapasitas", width: 16 },
        { header: "Kategori", key: "kategori", width: 22 },
        { header: "QR Code", key: "qr_code", width: 18 },
      ];
      worksheet.views = [{ state: "frozen", ySplit: 1 }];
      worksheet.getRow(1).height = 24;
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F2D5C" } };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      for (const [index, mt] of targetRows.entries()) {
        const excelRow = worksheet.addRow({
          no: index + 1,
          nopol: mt.nopol,
          cabang: mt.cabang,
          kapasitas: mt.kapasitas,
          kategori: mt.kategori,
          qr_code: "",
        });
        excelRow.height = 84;
        excelRow.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: cell.col === 6 ? "center" : "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        const qrDataUrlMt = await createQrDataUrl(mt.nopol);
        const imageId = workbook.addImage({ base64: qrDataUrlMt, extension: "png" });
        worksheet.addImage(imageId, {
          tl: { col: 5.18, row: excelRow.number - 0.92 },
          ext: { width: QR_EXCEL_IMAGE_SIZE_PX, height: QR_EXCEL_IMAGE_SIZE_PX },
          editAs: "oneCell",
        });
      }

      const scope = mode === "cabang"
        ? (cabangByKode.get(kodeCabang) || kodeCabang)
        : "SEMUA_MT";
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `QR_CODE_MT_${sanitizeFilename(scope)}_${Date.now()}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      showNotif("success", `Excel QR Code ${targetRows.length} MT berhasil dibuat`);
    } catch (err) {
      console.error(err);
      showNotif("error", err?.message || "Gagal membuat Excel QR Code MT");
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
        <div
          onClick={() => {
            const willOpen = !open;
            setActivePanel(willOpen ? panelKey : null);
            if (willOpen && !mtRows.length) loadMtRows();
          }}
          style={{ ...ADMIN_PANEL_BUTTON_STYLE, cursor: "pointer" }}
          className={ADMIN_PANEL_BUTTON_CLASS}
        >{label}</div>
        <div className="col-md-10 text-gray-600">{info}</div>
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-2">
              <label className="form-label mb-1">Mode</label>
              <select
                className="form-select form-select-sm"
                value={mode}
                disabled={loadingMt || loadingExport}
                onChange={(event) => handleModeChange(event.target.value)}
              >
                <option value="semua">Semua MT</option>
                <option value="cabang">MT Cabang</option>
                <option value="satuan">Nopol Tertentu</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Cabang</label>
              <select
                className="form-select form-select-sm"
                value={kodeCabang}
                disabled={loadingMt || loadingExport || mode !== "cabang"}
                onChange={(event) => setKodeCabang(event.target.value)}
              >
                <option value="">-Pilih Cabang-</option>
                {cabangOptions.map((item) => (
                  <option key={item.kode_cabang} value={item.kode_cabang}>{item.nama_cabang}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Nopol</label>
              <select
                className="form-select form-select-sm"
                value={idMt}
                disabled={loadingMt || loadingExport || mode !== "satuan"}
                onChange={(event) => setIdMt(event.target.value)}
              >
                <option value="">-Pilih Nopol-</option>
                {normalizedMtRows.map((item) => (
                  <option key={item.id_mt} value={item.id_mt}>{item.nopol}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-1">
              <label className="form-label mb-1">Kapasitas</label>
              <input className="form-control form-control-sm" value={selectedMt?.kapasitas || ""} readOnly placeholder="-" />
            </div>
            <div className="col-12 col-md-1">
              <label className="form-label mb-1">Kategori</label>
              <input className="form-control form-control-sm" value={selectedMt?.kategori || ""} readOnly placeholder="-" />
            </div>
            <div className="col-12 col-md-2">
              {mode === "satuan" ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm w-100"
                  disabled={loadingMt || loadingExport || !selectedMt}
                  onClick={handleDownloadSatuanPdf}
                >
                  {loadingExport ? "Membuat PDF..." : "Download PDF"}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-success btn-sm w-100"
                  disabled={loadingMt || loadingExport || (mode === "cabang" && !kodeCabang) || !targetRows.length}
                  onClick={handleDownloadMassalExcel}
                >
                  {loadingExport ? "Membuat Excel..." : "Download Excel"}
                </button>
              )}
            </div>
          </div>
          <div className="small text-muted mt-2">
            {loadingMt ? "Memuat daftar MT..." : `${targetRows.length} MT siap dibuat QR Code.`}
          </div>
        </div>
      </div>}
    </div>
  );
}

function AbsensiAdminDialog({ activePanel, setActivePanel, requestSubmitGuard }) {
  const panelKey = "absensi_admin";
  const open = activePanel === panelKey;
  const { showNotif } = useNotification();
  const info = "Lihat, koreksi, dan export data absensi mobile.";
  const label = "DASHBOARD ABSENSI ADMIN";
  const getTodayYmd = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const [tanggalDari, setTanggalDari] = useState(getTodayYmd);
  const [tanggalSampai, setTanggalSampai] = useState(getTodayYmd);
  const [usernameFilter, setUsernameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("MASUK");
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editTanggal, setEditTanggal] = useState("");
  const [editJamMasuk, setEditJamMasuk] = useState("");
  const [editKeterangan, setEditKeterangan] = useState("");
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const loadData = async () => {
    try {
      setLoadingList(true);
      const { data } = await api.get(`${backendPort}/api/admin/absensi`, {
        params: {
          tanggal_dari: tanggalDari || undefined,
          tanggal_sampai: tanggalSampai || undefined,
          username: usernameFilter || undefined,
          status_absen: statusFilter || undefined,
          limit: 300,
        },
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error(err);
      showNotif("error", err?.response?.data?.message || "Gagal memuat absensi");
    } finally {
      setLoadingList(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoadingExport(true);
      const res = await api.get(`${backendPort}/api/admin/absensi/export`, {
        params: {
          tanggal_dari: tanggalDari || undefined,
          tanggal_sampai: tanggalSampai || undefined,
          username: usernameFilter || undefined,
          status_absen: statusFilter || undefined,
        },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `absensi_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotif("success", "Export absensi berhasil");
    } catch (err) {
      console.error(err);
      showNotif("error", err?.response?.data?.message || "Export absensi gagal");
    } finally {
      setLoadingExport(false);
    }
  };

  const fillEditFromRow = (row) => {
    const id = Number(row?.id || 0);
    if (!id) return;
    const tanggalVal = String(row?.tanggal || "").slice(0, 10);
    const jamMasukVal = row?.jam_masuk || row?.["jam masuk"] || "";
    setEditingId(String(id));
    setEditTanggal(tanggalVal);
    // Scope saat ini: absen masuk saja.
    setEditJamMasuk(String(jamMasukVal || ""));
    setEditKeterangan(String(row?.keterangan || ""));
  };

  const submitKoreksi = async () => {
    try {
      if (!editingId) {
        showNotif("error", "Pilih baris absensi dulu dari tabel");
        return;
      }
      const passed = await requestSubmitGuard("Guard Submit: Koreksi Absensi");
      if (!passed) return;
      setLoadingSubmit(true);
      const payload = {
        id: Number(editingId),
        tanggal: editTanggal || undefined,
        status_absen: "MASUK",
        jam_masuk: editJamMasuk || null,
        keterangan: editKeterangan,
      };
      const { data } = await api.post(`${backendPort}/api/admin/absensi/koreksi`, payload);
      showNotif("success", data?.message || "Koreksi absensi berhasil");
      await loadData();
    } catch (err) {
      console.error(err);
      showNotif("error", err?.response?.data?.message || "Koreksi absensi gagal");
    } finally {
      setLoadingSubmit(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadData();
  }, [open]);

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
        <div
          onClick={() => {
            const willOpen = !open;
            setActivePanel(willOpen ? panelKey : null);
          }}
          style={{ ...ADMIN_PANEL_BUTTON_STYLE, cursor: "pointer" }}
          className={ADMIN_PANEL_BUTTON_CLASS}
        >{label}</div>
        <div className="col-md-10 text-gray-600">{info}</div>
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-2">
              <label className="form-label mb-1">Tanggal Dari</label>
              <input type="date" className="form-control form-control-sm" value={tanggalDari} onChange={(e) => setTanggalDari(e.target.value)} />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Tanggal Sampai</label>
              <input type="date" className="form-control form-control-sm" value={tanggalSampai} onChange={(e) => setTanggalSampai(e.target.value)} />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Username</label>
              <input className="form-control form-control-sm" value={usernameFilter} onChange={(e) => setUsernameFilter(e.target.value)} placeholder="username..." />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Status</label>
              <select className="form-select form-select-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="ALPA">Alpa</option>
                <option value="HADIR">Hadir</option>
                <option value="LIBUR">Libur</option>
                <option value="CUTI">Cuti</option>
                <option value="DINAS LUAR">Dinas Luar</option>
                <option value="IJIN">Ijin</option>
                <option value="SAKIT">Sakit</option>
                <option value="NON AKTIF">Non Aktif</option>
              </select>
            </div>
            <div className="col-2">
              <button className="btn btn-primary btn-sm w-100" onClick={loadData} disabled={loadingList}>
                {loadingList ? "Memuat..." : "Muat Data"}
              </button>
            </div>
            <div className="col-2">
              <button className="btn btn-success btn-sm w-100" onClick={handleExport} disabled={loadingExport}>
                {loadingExport ? "Export..." : "Export Excel"}
              </button>
            </div>
          </div>

          <div className="table-responsive mt-3" style={{ maxHeight: 260 }}>
            <table className="table table-sm table-bordered table-striped align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Tanggal</th>
                  <th>Username</th>
                  <th>Status</th>
                  <th>Jam Masuk</th>
                  <th>Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const id = Number(row?.id || 0);
                  const selected = String(id) === String(editingId);
                  return (
                    <tr
                      key={id || `${row?.username || "row"}-${idx}`}
                      style={{ cursor: "pointer", background: selected ? "#fff7d6" : undefined }}
                      onClick={() => fillEditFromRow(row)}
                    >
                      <td>{row?.id ?? ""}</td>
                      <td>{String(row?.tanggal || "").slice(0, 10)}</td>
                      <td>{row?.username ?? ""}</td>
                      <td>{row?.status_absen ?? ""}</td>
                      <td>{String(row?.jam_masuk || row?.["jam masuk"] || "")}</td>
                      <td>{row?.keterangan ?? ""}</td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">Tidak ada data.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="row g-2 align-items-end mt-2">
            <div className="col-1">
              <label className="form-label mb-1">ID</label>
              <input className="form-control form-control-sm" value={editingId} onChange={(e) => setEditingId(e.target.value)} />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Tanggal</label>
              <input type="date" className="form-control form-control-sm" value={editTanggal} onChange={(e) => setEditTanggal(e.target.value)} />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Jam Masuk</label>
              <input className="form-control form-control-sm" value={editJamMasuk} onChange={(e) => setEditJamMasuk(e.target.value)} placeholder="YYYY-MM-DD HH:MM:SS" />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Keterangan</label>
              <input className="form-control form-control-sm" value={editKeterangan} onChange={(e) => setEditKeterangan(e.target.value)} />
            </div>
            <div className="col-1">
              <button className="btn btn-warning btn-sm w-100" onClick={submitKoreksi} disabled={loadingSubmit}>
                {loadingSubmit ? "..." : "Koreksi"}
              </button>
            </div>
          </div>
        </div>
      </div>}
    </div>
  );
}

function Generate({ activePanel, setActivePanel }){
  const panelKey = "generate_saldo";
  const open = activePanel === panelKey;
  const [bulan, setBulan] = useState(0);
  const [tahun, setTahun] = useState(0);
  const { showNotif } = useNotification();
  const info="Generate Saldo Petty Kas Akhir Bulan ==> masukkan posisi bulan lalu/yang akan di rekap."; 
  const label="GENERATE SALDO";

  async function goGenerateSaldo(){
    try{
      setActivePanel(null);
      if(Number(bulan) === 0 || Number(tahun === 0)) {showNotif('error','Data Belum Lengkap'); return;}
      
      const kirimAPI = {
        bulan,
        tahun
      };
      console.log("kirimApi =", kirimAPI);
      await api.post(`${backendPort}/api/main/generateSaldoPetty`, kirimAPI);
        showNotif('success', `Generate bulan ${bulan}/${tahun} Saldo Berhasil`);
    }catch(err) {
       console.error(err);
      showNotif('error', `Generate Saldo Error`);
  }}
   
  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
      <div 
        onClick = {()=>setActivePanel(open ? null : panelKey)}
        style = {{ ...ADMIN_PANEL_BUTTON_STYLE, cursor : 'pointer'}}
        className={ADMIN_PANEL_BUTTON_CLASS}
      >{label}</div>
      <div className="col-md-10 text-gray-600">{info}</div>      
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-2">
              <label className="form-label mb-1">Bulan</label>
              <input
                className="form-control form-control-sm"
                placeholder="Bulan"
                value={Number(bulan)}
                onChange={e => {
                  setBulan(e.target.value);
                }}
              />
            </div>

            <div className="col-2">
              <label className="form-label mb-1">Tahun</label>
              <input
                className="form-control form-control-sm"
                placeholder="Tahun"
                value={Number(tahun)}
                onChange={e => {
                  setTahun(e.target.value);
                }}
              />
            </div>

            <div className="col"></div>

            <div className="col-2">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={() => goGenerateSaldo()}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
      }
    </div>
    )
}

function AdjustTanggal({ activePanel, setActivePanel }){
  const panelKey = "adjust_tanggal";
  const open = activePanel === panelKey;
  const [id, setId] = useState(0);
  const { showNotif } = useNotification();
  const info="Adjust kolom created_at di Jurnal agar disamakan dengan tanggal input transaksi."; 
  const label="ADJUST TANGGAL";

  async function adjustTanggal(){
    try{
      setActivePanel(null);
      if(Number(id) === 0) {showNotif('error','Data Belum Lengkap'); return;}
      
      const kirimAPI = {id};
      console.log("kirimApi =", kirimAPI);
      await api.post(`${backendPort}/api/main/adjust_tanggal`, kirimAPI);
        showNotif('success', `Adjust Tanggal Sukses`);
    }catch(err) {
       console.error(err);
      showNotif('error', `Adjust Tanggal Error`);
  }}
   
  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
      <div 
        onClick = {()=>setActivePanel(open ? null : panelKey)}
        style = {{ ...ADMIN_PANEL_BUTTON_STYLE, cursor : 'pointer'}}
        className={ADMIN_PANEL_BUTTON_CLASS}
      >{label}</div>
      <div className="col-md-10 text-gray-600">{info}</div>      
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-2">
              <label className="form-label mb-1">Id Jurnal</label>
              <input
                type="number"
                className="form-control form-control-sm"
                placeholder="ID Jurnal"
                value={Number(id)}
                onChange={e => {
                  setId(e.target.value);
                }}
              />
            </div>

            <div className="col"></div>

            <div className="col-2">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={() => adjustTanggal()}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
      }
    </div>
    )
}

function CetakAuditTrail({ activePanel, setActivePanel }){
  const panelKey = "cetak_audit_trail";
  const open = activePanel === panelKey;
  const [month, setMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [limit, setLimit] = useState("");
  const [reportMode, setReportMode] = useState("summary");
  const [auditId, setAuditId] = useState("");
  const [loading, setLoading] = useState(false);
  const { showNotif } = useNotification();
  const info="Cetak ringkasan audit trail berdasarkan periode atau detail payload berdasarkan ID.";
  const label="CETAK AUDIT TRAIL";

  async function handleDownloadAuditTrail() {
    try {
      if (reportMode === "detail" && !String(auditId || "").trim()) {
        showNotif("error", "ID audit wajib diisi untuk laporan detail.");
        return;
      }
      setLoading(true);
      const res = await api.post(
        `${backendPort}/api/admin/download_audit_trail`,
        {
          mode: reportMode,
          audit_id: reportMode === "detail" ? Number(auditId) : undefined,
          month: month || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          limit: reportMode === "summary" && String(limit).trim() ? Number(limit) : undefined,
        },
        { responseType: "blob" }
      );
      const isDetail = reportMode === "detail";
      const fileType = isDetail ? "application/json;charset=utf-8;" : "text/csv;charset=utf-8;";
      const fileName = isDetail
        ? `audit_trail_detail_${Number(auditId)}.json`
        : `audit_trail_ringkasan_${Date.now()}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: fileType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotif("success", "Download audit trail berhasil");
    } catch (err) {
      console.error(err);
      showNotif("error", err?.response?.data?.message || "Download audit trail gagal");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
        <div
          onClick={() => setActivePanel(open ? null : panelKey)}
          style={{ ...ADMIN_PANEL_BUTTON_STYLE, cursor: "pointer" }}
          className={ADMIN_PANEL_BUTTON_CLASS}
        >{label}</div>
        <div className="col-md-10 text-gray-600">{info}</div>
      </div>
      {open && <div className="row mt-2">
        <div className="w-100">
          <div className="row g-2 align-items-end">
            <div className="col-2">
              <label className="form-label mb-1">Jenis Laporan</label>
              <select
                className="form-select form-select-sm"
                value={reportMode}
                onChange={(e) => setReportMode(e.target.value)}
              >
                <option value="summary">Ringkasan Periode</option>
                <option value="detail">Detail Berdasarkan ID</option>
              </select>
            </div>
            {reportMode === "detail" && (
              <div className="col-2">
                <label className="form-label mb-1">ID Audit</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={1}
                  value={auditId}
                  onChange={(e) => setAuditId(e.target.value)}
                />
              </div>
            )}
            {reportMode === "summary" && (
              <>
            <div className="col-2">
              <label className="form-label mb-1">Bulan</label>
              <input
                type="month"
                className="form-control form-control-sm"
                value={month}
                onChange={(e) => {
                  const selectedMonth = String(e.target.value || "").trim();
                  setMonth(selectedMonth);
                  if (!selectedMonth) {
                    setStartDate("");
                    setEndDate("");
                    return;
                  }
                  const [yearRaw, monthRaw] = selectedMonth.split("-");
                  const year = Number(yearRaw);
                  const monthIndex = Number(monthRaw) - 1;
                  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
                    return;
                  }
                  const firstDay = new Date(year, monthIndex, 1);
                  const lastDay = new Date(year, monthIndex + 1, 0);
                  const today = new Date();
                  const sameYearMonth = today.getFullYear() === year && today.getMonth() === monthIndex;
                  const endTarget = sameYearMonth ? today : lastDay;
                  const toYmd = (d) => {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    return `${y}-${m}-${day}`;
                  };
                  setStartDate(toYmd(firstDay));
                  setEndDate(toYmd(endTarget));
                }}
              />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Tanggal Awal</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={Boolean(month)}
              />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Tanggal Akhir</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={Boolean(month)}
              />
            </div>
            <div className="col-2">
              <label className="form-label mb-1">Jumlah Baris</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min={1}
                max={10000}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              />
            </div>
              </>
            )}
            <div className="col-2">
              <button
                className="btn btn-primary btn-sm w-100"
                disabled={loading}
                onClick={handleDownloadAuditTrail}
              >
                {loading ? "DOWNLOADING..." : "DOWNLOAD"}
              </button>
            </div>
          </div>
        </div>
      </div>}
    </div>
  );
}

function Download_db({ activePanel, setActivePanel }){
  const panelKey = "download_rawdata";
  const open = activePanel === panelKey;
  const [tabel, setTabel] = useState("mt_profil");
  const [jumlahBaris, setJumlahBaris] = useState(1000);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const { showNotif } = useNotification();
  const info="Lihat dan download raw data dari database";
  const label="DOWNLOAD RAWDATA";
  const pilihanTabel = ["mt_profil", "mt_event", "mt_odo", "ban_profil", "ban_event", "ban_kmtd", "record"];

  function handleCleanWorkspace() {
    setTabel("mt_profil");
    setJumlahBaris(1000);
    setPreviewColumns([]);
    setPreviewRows([]);
    showNotif("success", "Workspace download raw data dibersihkan.");
  }

  async function handlePreview() {
    try {
      setLoadingPreview(true);
      const requestedRows = Number(jumlahBaris) || 1000;
      const { data } = await api.post(
        `${backendPort}/api/admin/preview_db`,
        {
          tabel,
          jumlah_baris: requestedRows,
          jumlahBaris: requestedRows,
          limit: requestedRows,
          from: 1,
          to: requestedRows
        }
      );
      const cols = Array.isArray(data?.columns) ? data.columns : [];
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setPreviewColumns(cols);
      setPreviewRows(rows);
      showNotif("success", `Preview ${tabel} berhasil dimuat (${rows.length} baris).`);
    } catch (e) {
      console.error(e);
      setPreviewColumns([]);
      setPreviewRows([]);
      showNotif("error", `Gagal preview data ${tabel}`);
    } finally {
      setLoadingPreview(false);
    }
  }

  async function handleDownload() {
    try{
      setLoadingDownload(true);
      const requestedRows = Number(jumlahBaris) || 1000;
      const res = await api.post(
        `${backendPort}/api/admin/download_db`,
        {
          tabel,
          jumlah_baris: requestedRows,
          jumlahBaris: requestedRows,
          limit: requestedRows,
          from: 1,
          to: requestedRows
        },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tabel}_${Number(jumlahBaris)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotif("success",`Berhasil download data ${tabel}`);
    } catch (e) {
      showNotif("error",`Gagal download data ${tabel}`);
      console.error(e);
    } finally {
      setLoadingDownload(false);
    }
  }

  function renderValue(v) {
    if (v === null || v === undefined) return "-";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  }

  return (
    <div className="px-3 py-2 rounded" style={ADMIN_PANEL_BOX_STYLE}>
      <div className="row col-md-12">
      <div 
        onClick = {()=>setActivePanel(open ? null : panelKey)}
        style = {{ ...ADMIN_PANEL_BUTTON_STYLE, cursor : 'pointer'}}
        className={ADMIN_PANEL_BUTTON_CLASS}
      >{label}</div>
      <div className="col-md-10 text-gray-600">{info}</div>      
      </div>
      {open && <div className="row mt-2">
        <div className="row g-2 align-items-end">
          <div className="col-md-2">
            <label className="text-sm text-gray-700">Tabel</label>
            <select
              className="form-control"
              value={tabel}
              onChange={(e) => setTabel(e.target.value)}
            >
              {pilihanTabel.map((namaTabel) => (
                <option key={namaTabel} value={namaTabel}>
                  {namaTabel}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="text-sm text-gray-700">Jumlah Baris</label>
            <input
              type="number"
              min={1}
              max={1000000}
              className="form-control"
              value={Number(jumlahBaris)}
              onChange={(e) => setJumlahBaris(e.target.value)}
            />
          </div>
          <div className="col"></div>
          <div className="col-md-4">
            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn btn-secondary"
                disabled={loadingPreview || loadingDownload}
                onClick={handleCleanWorkspace}
              >
                CLEAN
              </button>
              <button
                className="btn btn-success"
                disabled={loadingDownload}
                onClick={handleDownload}
              >
                {loadingDownload ? "DOWNLOADING..." : "DOWNLOAD"}
              </button>
              <button
                className="btn btn-primary"
                disabled={loadingPreview}
                onClick={handlePreview}
              >
                {loadingPreview ? "LOADING..." : "SUBMIT"}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="text-sm text-gray-600 mb-1">
            Preview `{tabel}` (urut DESC, max {Number(jumlahBaris)} baris)
          </div>
          <div className="table-responsive max-h-[420px] overflow-auto border rounded bg-white">
            <table className="table table-sm table-striped mb-0">
              <thead className="table-light">
                <tr>
                  {previewColumns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, idx) => (
                  <tr key={idx}>
                    {previewColumns.map((col) => (
                      <td key={`${idx}-${col}`}>{renderValue(row?.[col])}</td>
                    ))}
                  </tr>
                ))}
                {!previewRows.length && (
                  <tr>
                    <td className="text-center text-gray-500" colSpan={Math.max(previewColumns.length, 1)}>
                      Belum ada data preview.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      }
    </div>
    )
  
  }
